/**
 * Phase 8 follow-up (21-Apr-26) — Bulk-resume scoring bridge.
 *
 * `SmartMatchThread.resumeList` only stores file metadata (id, name,
 * sizeBytes, parseStatus) because localStorage can't safely hold
 * parsed resume bodies (each file is typically 5-20 KB of text and a
 * batch of 50 would blow through Chrome's 5 MB quota).
 *
 * The canvas, however, needs the parsed text to actually score the
 * uploads — without it we fall back to metadata-only paraphrases and
 * the user sees no matches after uploading resumes (the bug reported
 * 21-Apr-26: "on upload of resume i dont see the matching").
 *
 * This module is a thin sessionStorage-backed ring that the intake
 * page writes to on submit and the canvas reads once. sessionStorage
 * is tab-scoped (cleared on close) and has a higher per-origin quota
 * than localStorage in most browsers, so batches up to a few MB
 * survive the navigation.
 *
 * Entries are keyed by `threadId` so re-visiting a historical thread
 * after a tab reload gracefully degrades to "no live bodies" rather
 * than scoring the wrong thread's bytes.
 */

const KEY_PREFIX = 'ggj.smartMatch.pendingResumes.';
/** Per-file text cap — 30 KB is enough for a dense 5-page resume. */
const MAX_TEXT_CHARS = 30_000;
/** Total payload cap — guard against pathological batches. */
const MAX_BATCH_CHARS = 2_500_000;

export interface PendingResumeText {
  id: string;
  name: string;
  text: string;
}

function keyFor(threadId: string): string {
  return `${KEY_PREFIX}${threadId}`;
}

export function storePendingResumeTexts(
  threadId: string,
  entries: PendingResumeText[],
): void {
  if (typeof window === 'undefined') return;
  if (!threadId || entries.length === 0) return;
  let total = 0;
  const clamped: PendingResumeText[] = [];
  for (const e of entries) {
    if (!e.text) continue;
    const trimmed =
      e.text.length > MAX_TEXT_CHARS ? e.text.slice(0, MAX_TEXT_CHARS) : e.text;
    if (total + trimmed.length > MAX_BATCH_CHARS) break;
    clamped.push({ id: e.id, name: e.name, text: trimmed });
    total += trimmed.length;
  }
  if (clamped.length === 0) return;
  try {
    window.sessionStorage.setItem(keyFor(threadId), JSON.stringify(clamped));
  } catch {
    /* quota / private mode — canvas will just fall back to metadata. */
  }
}

export function readPendingResumeTexts(
  threadId: string,
): PendingResumeText[] {
  if (typeof window === 'undefined') return [];
  if (!threadId) return [];
  try {
    const raw = window.sessionStorage.getItem(keyFor(threadId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is PendingResumeText =>
          !!e &&
          typeof (e as PendingResumeText).id === 'string' &&
          typeof (e as PendingResumeText).text === 'string',
      )
      .slice(0, 100);
  } catch {
    return [];
  }
}

export function clearPendingResumeTexts(threadId: string): void {
  if (typeof window === 'undefined' || !threadId) return;
  try {
    window.sessionStorage.removeItem(keyFor(threadId));
  } catch {
    /* ignore */
  }
}
